import * as React from 'react';
import { FlatList, Platform, Pressable, ScrollView, View, Text } from 'react-native';
import { VictoryLabel, VictoryPie, VictoryLegend, VictoryContainer, VictoryChart, VictoryTheme } from 'victory-native';
import Legend from '../Legend';
import ChartStyles from '../chartStyles';
import { Tooltip } from 'react-native-paper';
import { useMemo } from 'react';
import { G, Svg } from 'react-native-svg';

interface inputData {
  name: string;
  value: number;
  color: string;
}

interface Data {
  x: string;
  y: number;
}

interface prop {
  input: inputData[];
  donutRadius?: number;
  containerWidth?: number;
  legendFontColor?: string;
  paginateLegend?: boolean;
}
const PieChart: React.FC<prop> = ({ input, donutRadius, containerWidth, legendFontColor, paginateLegend = false }) => {
  const [data, setData] = React.useState<Data[]>([]);
  const [colors, setColors] = React.useState<string[]>([]);
  const [page, setPage] = React.useState(0);
  //const legendColW = containerWidth ? Math.max(150, Math.floor(containerWidth * 0.38)) : 0;
  const hasData = data && data.length > 0;

  const items = useMemo(() => {
    if (!hasData) return [];
    return [...input].filter(i => i?.value > 0).sort((a, b) => b.value - a.value);
  }, [hasData, input]);

  const pageSize = 7;
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const pageItems = items.slice(page * pageSize, (page + 1) * pageSize);
  React.useEffect(() => {
    if (input && input.length > 0) {
      const tempData = input.map(obj => {
        return { x: obj.name, y: obj.value }
      });
      setData(tempData);

      const tempColors = input.map(obj => {
        return obj.color;
      });
      setColors(tempColors);
    }
    else {
      setData([]);
    }

  }, [input])
  const legendColW = containerWidth ? Math.max(150, Math.floor(containerWidth * 0.38)) : 0;
  const pieColW = containerWidth ? containerWidth - legendColW - 10 : containerWidth;

  const total = items.reduce((sum, i) => sum + (i.value ?? 0), 0);

  const pctOf = (v: number) => (total > 0 ? (v / total) * 100 : 0);
  const fmtPct = (p: number) => `${Math.round(p)}%`; // or p.toFixed(1)+"%"
  const cx = pieColW ? pieColW / 2 : 175;
  const cy = 140;
  const LegendRow = ({ item }: { item: any }) => {
    const fullText = `${item.name}: ${item.value} (${fmtPct(pctOf(item.value))})`;
    return (
      <Tooltip title={fullText}>
        <Pressable style={{ flexDirection: "row", alignItems: "flex-start", marginBottom: 10 }}>
          <View style={{ width: 10, height: 10, borderRadius: 6, backgroundColor: item.color, marginRight: 8, marginTop: 2 }} />
          <Text numberOfLines={2} style={{ fontSize: 12, flex: 1 }}>
            {fullText}
          </Text>
        </Pressable>
      </Tooltip>
    );
  };

  const minLabelPct = 5;
  return (
    <View style={{ flexDirection: "row", height: 300 }}>
      <View style={{ width: pieColW }}>
        <Svg
          id={`${"chart"}-s-svg`}             // ✅ THIS is what runExport grabs
          width={pieColW}
          height={280}
          viewBox={`0 0 ${pieColW} ${280}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ overflow: "hidden" }}
        >
          <G>
            <VictoryPie
              standalone={false}
              animate={{ duration: 1000 }}
              data={hasData ? data : [{ x: "No Data", y: 1 }]}
              colorScale={hasData ? colors : ["#f0f0f0"]}
              innerRadius={donutRadius ? 30 : 50}
              style={{ labels: { display: "none" } }}
              // Control pie placement so legend fits below
              origin={{ x: cx, y: cy }}
              width={pieColW}
              height={280}
              // labelIndicator
              // labelIndicatorInnerOffset={10}
              // theme={VictoryTheme.material}
              // labelRadius={donutRadius * 0.72}

            />
            <VictoryLabel
              text={`${total}`}
              x={cx}
              y={cy - 8}
              textAnchor="middle"
              style={{ fontSize: 18, fontWeight: "700", fill: "#111" }}
            />
            <VictoryLabel
              text="Total"
              x={cx}
              y={cy + 12}
              textAnchor="middle"
              style={{ fontSize: 12, fill: "#666" }}
            />
          </G>
        </Svg>
      </View>
      {/* GAP */}
      <View style={{ width: 10 }} />

      {/* RIGHT: LEGEND */}
      <View style={{ width: legendColW, paddingRight: 6, paddingTop: 10 }}>
        {paginateLegend ? (
          <>
            <FlatList
              data={pageItems}
              keyExtractor={(it, idx) => `${it.name}-${idx}`}
              renderItem={({ item }) => <LegendRow item={item} />}
            />
            {pageCount > 1 && (
              <View style={{ flexDirection: "row", justifyContent: "space-between", paddingTop: 10 }}>
                <Pressable onPress={() => setPage(p => (p - 1 + pageCount) % pageCount)}>
                  <Text style={{ fontSize: 12 }}>Prev</Text>
                </Pressable>
                <Text style={{ fontSize: 11, color: "gray" }}>{`Page ${page + 1}/${pageCount}`}</Text>
                <Pressable onPress={() => setPage(p => (p + 1) % pageCount)}>
                  <Text style={{ fontSize: 12 }}>Next</Text>
                </Pressable>
              </View>
            )}
          </>
        ) : (
          <ScrollView style={{ height: 280 }} showsVerticalScrollIndicator={false}>
            {items.map((item, idx) => <LegendRow key={`${item.name}-${idx}`} item={item} />)}
          </ScrollView>
        )}
      </View>
    </View>
  )
}

export default PieChart;
